import Cursor from "./cursor";
import { Drawable } from "./drawable";
import { easeInOutQuart } from 'easing-utils'
import RenderableSegmentationMask from "./segmentationMask";


export class ScreenState{
  devlopmentScreenWidth = 1420;
  devlopmentScreenHeight = 730;
  /**
   * Initialises a Screen State
   * @param {CanvasRenderingContext2D} canvasCtx The Canvas Rendering Context
   * @param {number} animationStep The animation timeline stepping
   * @param {number} transitionProgress Screen Intro and Outro Transition Progress ranging from 0 to 1
   * @param {number} width The screen width
   * @param {number} height The screen height
   * @param {number} offsetX The screen offset x coordinate. Defaults to 0 if not specified.
   * @param {number} offsetY The screen offset y coordinate. Defaults to 0 if not specified.
   * @param {'touch' | 'cursor' | 'webcam'} gameMode The current mode of the game.
   * @param {number|undefined} playerPosition The current player position if avalable
   */
  constructor(canvasCtx, animationStep, transitionProgress, width, height, offsetX, offsetY, gameMode, lang, playerPosition){
    this.canvasCtx = canvasCtx;
    this.animationStep = animationStep;
    this.transitionProgress = transitionProgress;
    this.width = width;
    this.height = height;
    this.offsetX = offsetX || 0;
    this.offsetY = offsetY || 0; 
    this.lang = lang;
    this.gameMode = gameMode;
    this.playerPosition = playerPosition;
  }

  /**
   * Changes the x coordinate based on the current screen's dimentions. 
   * Cannot be used for width.
   * @param {number} value The x coordinate
   * @returns {number} The resized x coordinate value
   */
  X(value){
    // return value;
    return ((value/this.devlopmentScreenWidth)*this.width) + this.offsetX;
  }

  // /**
  //  * Changes the width based on the current screen's dimentions. 
  //  * Cannot be used for x coordinate.
  //  * @param {number} value The width
  //  * @returns {number} The resized width value
  //  */
  // W(value){
  //   // return value;
  //   return ((value/this.devlopmentScreenWidth)*this.width);
  // }

  /**
   * Changes the y coordinate based on the current screen's dimentions. 
   * Cannot be used for height.
   * @param {number} value The y coordinate
   * @returns {number} The resized y coordinate value
   */
  Y(value){
    // return value;
    return ((value/this.devlopmentScreenHeight)*this.height) + this.offsetY;
  }

  // /**
  //  * Changes the height based on the current screen's dimentions. 
  //  * Cannot be used for y coordinate.
  //  * @param {number} value The height
  //  * @returns {number} The resized height value
  //  */
  // H(value){
  //   // return value;
  //   return ((value/this.devlopmentScreenHeight)*this.height);
  // }


  /**
   * Changes the size based on the current screen's dimentions via area.
   * @param {number} value The size for font size, circle radius, etc.
   * @returns {number} The resized size value
   */
  S(value){
    // return value;
    return (value/(this.devlopmentScreenWidth*this.devlopmentScreenHeight))*(this.width*this.height);
  }

  /**
   * Changes the x, y, width, and height for a rectangle so that it displays properly on diffrent screen dimentions.
   */
  rectResize(x, y, width, height, useTransitionProgress){
    const transitionProgressFinal = useTransitionProgress ? this.transitionProgress : 1
    const [finalWidth, finalHeight] = [width*transitionProgressFinal, height*transitionProgressFinal]
    const [finalRenderableWidth, finalRenderableHeight] = [this.S(finalWidth), this.S(finalHeight)]
    const [newX, newY] = [this.X(x + finalWidth/2) - finalRenderableWidth/2, this.Y(y+finalHeight/2) - finalRenderableHeight/2]
    return [newX, newY, finalRenderableWidth, finalRenderableHeight]
  }

  rectResizeV2Static(x, y, width, height){
    const scalingWidth = (width/this.devlopmentScreenWidth*this.width)/width
    const scalingHeight = (height/this.devlopmentScreenHeight*this.height)/height
    const scaling = Math.max(scalingWidth, scalingHeight);
    return [this.X(x), this.Y(y), width*scaling, height*scaling]
    
  }

  /**
   * For collision detection only, changes cursor coordinates and size based on the current screen size
   * @param {Cursor[]} cursors The list of cursors to change
   * @returns {Cursor[]} A list of changed cursors
   */
  fixCursor(cursors){
    const newCursors = new Array();
    for (let i = 0; i < cursors.length; i++){
      newCursors.push(
        new Cursor(
          (cursors[i].x - this.offsetX)/this.width * this.devlopmentScreenWidth,
          (cursors[i].y - this.offsetY)/this.height * this.devlopmentScreenHeight,
          cursors[i].type,
          cursors[i].radius
        )
      )
    }
    return newCursors;
  }
}


class Screen{
  transitionState = /** @type {'intro' | 'idle' | 'outro'}*/ ('idle');
  transitionDuration = 750;
  transitionStartTimestamp = /** @type {number | null}*/ (null);

  /**
  * Initialiases screen object.
  * @param {ScreenState} screenState The current state of the screen
  * @param {Drawable[]} [drawables = []] Drawable Elements to put in the screen
  */
  constructor(screenState, drawables){
    this.screenState = screenState;
    this.drawables = drawables;
    this.transitionProgress = screenState.transitionProgress;
  }

  /**
   * Adds a new drawable to the screen.
   * @param {Drawable} drawable The drawable element to add to the screen.
   */
  push(drawable){
    this.drawables.push(drawable);
  }

  /**
   * Renders the screen
   * @param {number} animationStep 
   * @param {Cursor[]} cursors A list of cursor objects
   * @param {number|undefined} playerPosition The current player position if avalable
   * @param {RenderableSegmentationMask | undefined} segmentationMask The Segmentation Mask if avaliable
   */
  render(animationStep, cursors, playerPosition, segmentationMask){
    // Handle Transitions
    if (this.transitionStartTimestamp === -1){
      this.transitionStartTimestamp = animationStep;
    }
    if (this.transitionState !== 'idle' && this.transitionStartTimestamp < animationStep){
      if (animationStep - this.transitionStartTimestamp >= this.transitionDuration){
        // Ensure transitionProgress at max/min
        this.transitionProgress = this.transitionState === 'intro' ? 1 : 0;
        this.transitionState = 'idle'
      }
      else if (this.transitionState === 'intro'){
        this.transitionProgress = easeInOutQuart(Math.min((animationStep - this.transitionStartTimestamp)/this.transitionDuration, 1));
      }
      else if (this.transitionState === 'outro'){
        this.transitionProgress = easeInOutQuart(Math.max(1-((animationStep - this.transitionStartTimestamp)/this.transitionDuration), 0));
      }
    }

    // Render Objects
    this.screenState = new ScreenState(
      this.screenState.canvasCtx, animationStep, 
      this.transitionProgress, this.screenState.width, this.screenState.height, 
      this.screenState.offsetX, this.screenState.offsetY, this.screenState.gameMode, this.screenState.lang, playerPosition
    );
    // const newCursors = this.screenState.fixCursor(cursors);

    const removableDrawableIndexes = [];
    for (let i = 0; i < this.drawables.length; i++){
      const drawableToRender = this.drawables[i];
      if (drawableToRender.destroyed){
        removableDrawableIndexes.push(i);
      }
      else{
        if (drawableToRender.updateHover){
          drawableToRender.updateHover(cursors, this.screenState);
        }
        if (drawableToRender.takesSegmentationMask){
          drawableToRender.render(this.screenState, segmentationMask);
        }
        else{
          drawableToRender.render(this.screenState);
        }
      }
    }
    for (let i = 0; i < removableDrawableIndexes.length; i++){
      this.drawables.splice(removableDrawableIndexes[i] - i, 1);
    }
  }

  /**
   * Starts the Screen's Intro transition
   * @param {number} [animationStep=-1] The current animation timestamp
   */
  startIntroTransition(animationStep){
    this.transitionStartTimestamp = animationStep || -1;
    this.transitionState = 'intro'
  }

  /**
   * Starts the Screen's Outro transition
   * @param {number} [animationStep=-1] The current animation timestamp
   */
  startOutroTransition(animationStep){
    this.transitionStartTimestamp = animationStep || -1;
    this.transitionState = 'outro'
  }
}


export default Screen;